"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import HeroSection from "@/components/hero-section";
import BlogClientWrapper from "./blog-client-wrapper";
import { ViewType, FilterType } from "./search-filter-bar";
import Link from "next/link";
import cn from "@/utils/cn";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { BlogPostDocument } from "../../../../../prismicio-types";

interface BlogPageClientProps {
  content: any;
  blogContents: BlogPostDocument[];
  initialSearchQuery: string;
  initialFilterType: FilterType;
  initialViewType: ViewType;
  totalResults: number;
  sanitizedPageNumber: number;
  pageNumbers: number[];
  focusSearch?: boolean;
}

export default function BlogPageClient({
  content,
  blogContents,
  initialSearchQuery,
  initialFilterType,
  initialViewType,
  totalResults,
  sanitizedPageNumber,
  pageNumbers,
  focusSearch,
}: BlogPageClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);

  // Update URL when search changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());

    if (searchQuery !== initialSearchQuery) {
      if (searchQuery) {
        params.set("search", searchQuery);
      } else {
        params.delete("search");
      }

      // Reset to page 1 when search changes
      params.delete("page");

      const newUrl = params.toString() ? `?${params.toString()}` : "";
      router.push(`/resources${newUrl}`, { scroll: false });
    }
  }, [searchQuery, initialSearchQuery, router, searchParams]);

  // Helper function to build URLs with search parameters
  const buildUrl = (page: number) => {
    const params = new URLSearchParams();
    if (page > 1) params.set("page", page.toString());
    if (searchQuery) params.set("search", searchQuery);
    if (initialFilterType !== "all") params.set("filter", initialFilterType);
    if (initialViewType !== "card") params.set("view", initialViewType);
    return `/resources${params.toString() ? `?${params.toString()}` : ""}`;
  };

  return (
    <main className="w-full">
      <HeroSection
        heroTitle={content.data.hero_title}
        heroDescription={content.data.hero_description}
        backgroundImage={content.data.hero_background_image}
        backgroundWithGradient={true}
        className="bg-[#FAFAFA]"
        showSearch={true}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search the Engine"
        searchDescription="Explore our curated collection of resources and helpful articles on email marketing."
        totalResults={totalResults}
        searchFocused={focusSearch}
      >
        <div className="w-full">
          <BlogClientWrapper
            blogContents={blogContents}
            initialSearchQuery={searchQuery}
            initialFilterType={initialFilterType}
            initialViewType={initialViewType}
          />
        </div>

        {/* Pagination controls */}
        <div className="mb-[48px] hidden w-full flex-row items-center justify-center space-x-6 sm:flex xl:mb-10">
          <Link
            href={buildUrl(sanitizedPageNumber - 1)}
            className={cn(
              "flex items-center justify-center p-2 text-[#979797]",
              sanitizedPageNumber === 1 && "pointer-events-none",
            )}
          >
            <IoIosArrowBack className="text-kd-secondary-3" />
          </Link>
          {pageNumbers.map((page) => (
            <Link
              href={buildUrl(page)}
              key={page}
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-[10px]  text-sm leading-6",
                sanitizedPageNumber == page
                  ? "bg-kd-lte-blue text-white"
                  : "text-[#222B45] hover:bg-[#e0e0e0]",
              )}
            >
              <span>{page}</span>
            </Link>
          ))}
          <Link
            href={buildUrl(sanitizedPageNumber + 1)}
            className={cn(
              "text-[#979797] flex items-center justify-center p-2",
              sanitizedPageNumber === pageNumbers.length &&
                "pointer-events-none",
            )}
          >
            <IoIosArrowForward className="text-kd-secondary-3" />
          </Link>
        </div>
      </HeroSection>
    </main>
  );
}
